import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import linkedinLogo from '../../assets/LI-In-Bug.png';
import '../../styles/Team.css';
import PositionConstants from '../../constants/positions';
import '../../styles/_colors.css';
import '../../styles/_typography.css';
import '../../styles/main.css';

import { apiFetch } from '../../utils/api';

export default function QuantClub() {
  const [members, setMembers] = useState([]);
  const [clubImage, setClubImage] = useState("");
  const aboutRef = useRef(null);
  const valuesRef = useRef(null);
  const membersRef = useRef(null);

  useEffect(() => {
    // Fetch Quant Club from backend and display only its members and image
    apiFetch('/api/quant-club')
      .then(data => {
        // If multiple clubs, pick the first (should only be one)
        const club = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setMembers(club ? club.members : []);
        setClubImage(club && club.image ? club.image : "");
      })
      .catch(() => {
        setMembers([]);
        setClubImage("");
      });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const refs = [aboutRef, valuesRef, membersRef];
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div className="team-container">
      <Helmet>
        <title>NHHS Analytics | Quant Club</title>
      </Helmet>
      {/* Hero section */}
      <section className="team-hero">
        <div className="team-hero-content">
          <h1>Quant Club</h1>
          <p>
            Quant Club a specialized subgroup within NHHS Analytics, focuses on quantitative analysis and financial modeling. 
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">{members.length}</div>
              <div className="stat-label">Quant Members</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">2025</div>
              <div className="stat-label">Founded</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Potential</div>
            </div>
          </div>
        </div>
      </section>

      {/* About section */}
      <section className="team-about-section" ref={aboutRef}>
        <div className="team-about-container">
          <div className="team-about-content">
            <h2>What We Do</h2>
            <div className="team-about-text">
              <p>
                Quant Club is a vibrant community for students passionate about finance, mathematics, and programming. We work on real-world projects, participate in technical workshops, and collaborate with industry professionals to push the boundaries of quantitative analysis and financial modeling.
              </p>
              <p>
                Our members explore topics like algorithmic trading, portfolio optimization, risk management, and macroeconomic analysis. Whether you're interested in coding trading algorithms, analyzing financial data, or learning advanced statistics, you'll find opportunities to grow and contribute.
              </p>
              <p>
                We welcome students from all backgrounds—no prior experience required, just curiosity and a willingness to learn. Join us to develop cutting-edge skills in quantitative finance and be part of a team that values innovation, collaboration, and continuous learning.
              </p>
              <div className="team-about-buttons">
                <a href="/contact" className="team-btn primary">
                  Join Quant Club
                </a>
                <a href="/projects?category=Quant Club" className="team-btn secondary">
                  See Our Projects
                </a>
              </div>
            </div>
          </div>
          <div className="team-about-image">
            <img
              src={clubImage || "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=600&q=80"}
              alt="Quant Club Team"
              style={{ maxWidth: 320, borderRadius: 12, margin: "0 auto" }}
            />
          </div>
        </div>
      </section>

      {/* Values/Focus section */}
      <section className="team-values-section" ref={valuesRef}>
        <div className="team-values-container">
          <div className="team-values-header">
            <h2>Our Focus</h2>
            <p>
              Quant Club explores topics such as:
            </p>
          </div>
          <div className="team-values-grid">
            <div className="value-card">
              <h3>Algorithmic Trading</h3>
              <p>Design, test, and deploy trading strategies using real market data.</p>
            </div>
            <div className="value-card">
              <h3>Financial Modeling</h3>
              <p>Build models for portfolio optimization, risk management, and forecasting.</p>
            </div>
            <div className="value-card">
              <h3>Programming & Data Science</h3>
              <p>Learn Python, R, and other tools for quantitative analysis and visualization.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quant Club members section */}
      <section className="team-members-section" ref={membersRef}>
        <div className="team-members-container">
          <h2>Quant Club Members</h2>
          <p className="team-section-subtitle">
            Meet the students driving quantitative innovation at NHH
          </p>
          <div className="team-members-grid">
            {members.length === 0 ? (
              <div style={{ color: '#888', fontStyle: 'italic', textAlign: 'center', width: '100%' }}>
                No members listed yet. Want to join? <a href="/contact">Contact us!</a>
              </div>
            ) : (
              // Sort members by highest priority quant position, then name
              [...members]
                .slice()
                .sort((a, b) => {
                  // Helper to get highest priority quant position index
                  function getPriority(member) {
                    let positionsArr = Array.isArray(member.positions)
                      ? member.positions
                      : Array.isArray(member.position)
                      ? member.position
                      : member.position
                      ? [member.position]
                      : [];
                    positionsArr = positionsArr.filter(Boolean);
                    // Only quant/member positions
                    const filtered = positionsArr.filter(
                      pos =>
                        typeof pos === 'string' &&
                        (pos.toLowerCase().includes('quant') || pos.toLowerCase() === 'member')
                    );
                    // Find the lowest index in PositionConstants.OPTIONS
                    let minIdx = 999;
                    filtered.forEach(pos => {
                      const idx = PositionConstants.OPTIONS.findIndex(
                        opt => opt.toLowerCase() === pos.toLowerCase()
                      );
                      if (idx !== -1 && idx < minIdx) minIdx = idx;
                    });
                    return minIdx;
                  }
                  const idxA = getPriority(a);
                  const idxB = getPriority(b);
                  if (idxA !== idxB) return idxA - idxB;
                  // If same priority, sort by name
                  return (a.name || '').localeCompare(b.name || '');
                })
                .map(member => (
                  <div className="team-member-card" key={member._id || member.name}>
                    <div className="team-member-photo-wrapper">
                      <img
                        className="team-member-photo"
                        src={member.image || '/default-profile.svg'}
                        alt={member.name}
                        loading="lazy"
                      />
                    </div>
                    <div className="team-member-info">
                      <h4 className="team-member-name">{member.name}</h4>
                      {/* Display Quant-related positions, comma separated, or fallback */}
                      {(() => {
                        let positionsArr = Array.isArray(member.positions)
                          ? member.positions
                          : Array.isArray(member.position)
                          ? member.position
                          : member.position
                          ? [member.position]
                          : [];
                        positionsArr = positionsArr.filter(Boolean);
                        // Only show positions containing 'quant' or exactly 'member' (case-insensitive)
                        const filtered = positionsArr.filter(
                          pos =>
                            typeof pos === 'string' &&
                            (pos.toLowerCase().includes('quant') || pos.toLowerCase() === 'member')
                        );
                        // Sort filtered positions by their order in PositionConstants.OPTIONS (case-insensitive)
                        const sorted = filtered
                          .slice()
                          .sort((a, b) => {
                            const idxA = PositionConstants.OPTIONS.findIndex(
                              opt => opt.toLowerCase() === a.toLowerCase()
                            );
                            const idxB = PositionConstants.OPTIONS.findIndex(
                              opt => opt.toLowerCase() === b.toLowerCase()
                            );
                            return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
                          });
                        return (
                          <>
                            <span className="team-member-role">{sorted.length > 0 ? sorted.join(', ') : 'Member'}</span>
                            {member.linkedin && (
                              <div className="team-member-linkedin-wrapper">
                                <a
                                  href={member.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="team-member-linkedin"
                                  aria-label={`${member.name}'s LinkedIn profile`}
                                >
                                  <img
                                    src={linkedinLogo}
                                    alt="LinkedIn"
                                    className="team-member-linkedin-logo"
                                  />
                                </a>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
